import { ReactNode, ComponentType } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import Lozenge from "@atlaskit/lozenge";

import { Banner } from "@/components/adg4/price-card/src/banner";
import { ContentStack } from "@/components/adg4/price-card/src/content-stack";
import { ContentText } from "@/components/adg4/price-card/src/content-text";
import { ExpanderStack } from "@/components/adg4/price-card/src/expander-stack";
import {
    PriceCard as BasePriceCard,
    getPriceCardProps
} from "@/components/adg4/price-card/src/price-card";
import { PriceCardSection } from "@/components/adg4/price-card/src/price-card-section";
import {
    CloudOfferingsWithSkus,
    PlanFeatureMessage,
    PricingPeriod,
    CloudProductKey
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import { PlanFeature } from "@/components/wpl/pricing/src/price-finder/price-card/price-card-plan-feature.tsx";
import { usePriceFinderContext } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { wacToken } from "@/libraries/design/foundations/index.ts";

import { Price } from "./price.tsx";
import { StickyCTAContextProvider } from "./sticky-cta-context.tsx";

const planFeatureMessageContainerStyles = css({
    padding: "0",
    marginBlockStart: "0",
    listStyleType: "none"
});
const planFeatureMessagesStyles = css({
    "marginBlockStart": "16px",
    "&:nth-of-type(1)": {
        marginBlockStart: "0px"
    }
});

// @ts-ignore
const stickyCtaContainerStyles = css({
    display: "flex",
    gap: "24px",
    flexDirection: "column",
    alignItems: "center"
});

/**
 * @deprecated - Please use @/libraries/design/components/pricing-card instead
 */
export interface PriceCardProps {
    first?: boolean;
    last?: boolean;
    highlighted: boolean;
    planName: string;
    planTitleComponent?: ComponentType<{ planName: string }>;
    description?: string;
    adjacentLowerTierPlanName?: string;
    planFeatureMessages?: PlanFeatureMessage[];
    ctaButton: ReactNode;
    billingCycle: PricingPeriod;
    overSignupLimit: boolean;
    currencyISOCode: string;
    unitCount: number;
    productKey: string;
    priceCalcIdx: number;
    contactURL: string;
    offeringSku?: string;
    enableRecommendation?: boolean;
    shouldAddDiscount?: boolean;
    pricingPlanId: string;
    edition: string | undefined;
    dataProductKey: CloudProductKey;
}

/**
 * @deprecated - Please use @/libraries/design/components/pricing-card instead
 */
// eslint-disable-next-line complexity
export const PriceCard = (props: PriceCardProps) => {
    const { enableRecommendation = true } = props;

    const { recommendationElement } = usePriceFinderContext();

    let highlightedFeaturesTitle = (
        <FormattedMessage
            id="Pricing.PriceCard.featureMessageTitleFree"
            defaultMessage="Includes:"
        />
    );
    if (props.adjacentLowerTierPlanName) {
        highlightedFeaturesTitle = (
            <FormattedMessage
                id="Pricing.PriceCard.featureMessageTitle"
                defaultMessage="Everything from {featureMessagePlanName}, plus:"
                values={{
                    featureMessagePlanName: props.adjacentLowerTierPlanName
                }}
            />
        );
    }

    const planTitleId = `planTitle_${props.priceCalcIdx}`;

    const defaultRecommendationElement = (
        <FormattedMessage
            id="Pricing.PriceCard.recommended"
            defaultMessage="Recommended"
        />
    );

    /* eslint-disable jsx-a11y/no-redundant-roles */
    /* added role="list" to ul for safari https://developer.mozilla.org/en-US/docs/Web/CSS/list-style#accessibility  */
    return (
        <BasePriceCard
            {...getPriceCardProps(props)}
            outlined={props.highlighted}
        >
            <PriceCardSection>
                {enableRecommendation && (
                    <Banner shown={props.highlighted}>
                        {recommendationElement
                            ? recommendationElement
                            : defaultRecommendationElement}
                    </Banner>
                )}
                <ContentStack>
                    <div>
                        <ContentText id={planTitleId} variant="lg" as="h2">
                            <PriceCardTitle
                                planName={props.planName}
                                planTitleComponent={props.planTitleComponent}
                            />
                        </ContentText>
                        {props.description && (
                            <ContentText
                                variant="md"
                                id={`pricing-plan-description-${props.planName}`}
                            >
                                {props.description}
                            </ContentText>
                        )}
                    </div>
                    <div
                        css={css({
                            marginBlockStart: "auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "24px"
                        })}
                    >
                        <div css={css({ height: "68px" })}>
                            <Price {...props} />
                        </div>

                        <div
                            css={[
                                props.billingCycle === "ANNUAL" &&
                                    !(
                                        props.offeringSku ===
                                            CloudOfferingsWithSkus.rovo &&
                                        props.billingCycle === "ANNUAL" &&
                                        props.unitCount > 1000
                                    ) &&
                                    css({ marginBlockStart: "24px" })
                            ]}
                        >
                            <StickyCTAContextProvider isSticky={false}>
                                {props.ctaButton}
                            </StickyCTAContextProvider>
                        </div>
                    </div>
                </ContentStack>
            </PriceCardSection>
            {!!props.planFeatureMessages?.length && (
                <PriceCardSection>
                    <ExpanderStack
                        heading={highlightedFeaturesTitle}
                        lozenge={
                            props.dataProductKey === "loom" &&
                            props.planName === "Business + AI" && (
                                <span
                                    css={css({
                                        "marginBlockStart": "-4px",
                                        // needed because Lozenge supports backgroundColor, but not background which is needed for gradient
                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                        "> span": {
                                            background:
                                                // eslint-disable-next-line local/ensure-wac-token-usage
                                                "linear-gradient(90deg, #2684FF 0%, #BF63F3 100%)"
                                        }
                                    })}
                                >
                                    <Lozenge
                                        style={{
                                            color: wacToken(
                                                "color.neutral.light0"
                                            )
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Pricing.PriceCard.LoomAiLozenge"
                                            defaultMessage="Powered by Loom AI"
                                            description="Loom AI should not be translated"
                                        />
                                    </Lozenge>
                                </span>
                            )
                        }
                    >
                        <ul css={planFeatureMessageContainerStyles} role="list">
                            {props.planFeatureMessages.map(
                                (planFeatureMessage, idx) => {
                                    return (
                                        <li
                                            key={idx}
                                            css={planFeatureMessagesStyles}
                                        >
                                            <PlanFeature
                                                {...planFeatureMessage}
                                                padded={false}
                                            />
                                        </li>
                                    );
                                }
                            )}
                        </ul>
                    </ExpanderStack>
                </PriceCardSection>
            )}
        </BasePriceCard>
    );
};

interface PriceCardTitleProps {
    planName: string;
    planTitleComponent?: ComponentType<{ planName: string }>;
}

const PriceCardTitle = (props: PriceCardTitleProps) => {
    const { planName, planTitleComponent: PlanTitle } = props;
    return PlanTitle ? (
        <PlanTitle planName={planName} />
    ) : (
        <span id={`pricing-plan-title-${planName}`}>{planName}</span>
    );
};
